/**
 * @file auth.reducer.js
 * @description
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since Dec 09, 2021 20:02
 */
import {
    removeToken,
    saveToken
} from 'src/content/Auth/authUtilities';

import { objectExists, objectHas } from 'src/@common/utilities';

import * as Actions from 'src/store/actions/auth.actions';

const initialState = {
    // general
    authStage: 'pw',  // pw, otp

    // login form
    serverError: '',

    // otp 2fa form
    otpAuthUrl: '',
    qrcImage: null,

    // authenticated user
    account: {},
    user: {},
    nodeEnv: '',
    authenticated: false,

    // standard stuff
    errorExists: false,
    serverMessage: '',
};

const authReducer = (state = initialState, action) => {
    let account = null;
    let authObj = null;
    let authenticated = null;
    let nodeEnv = null;
    let otpAuthUrl = null;
    let qrcImage = null;
    let user = null;

    switch (action.type) {

        // -------------------------------------------------------------------
        // setters
        // -------------------------------------------------------------------
        case Actions.AUTH_RESET: {
            return {
                ...state,
                account: {},
                user: {},
                authenticated: false,
                authStage: "pw",
            };
        }

        case Actions.AUTH_STAGE_SET: {
            return {
                ...state,
                authStage: action.payload,
            };
        }

        // -------------------------------------------------------------------
        // login.password
        // -------------------------------------------------------------------
        case Actions.AUTH_LOGIN_PASSWORD_REQUEST: {
            return {
                ...state,
                account: {},
                user: {},
                nodeEnv: '',
                authenticated: false,
                authStage: 'pw',
            };
        }

        case Actions.AUTH_LOGIN_PASSWORD_SUCCESS: {
            authObj = action.payload.authObj;
            nodeEnv = action.payload.nodeEnv;

            if (authObj.action === 'authenticated') {

                account = {
                    accountName: action.payload.authObj.authUser.account.accountName,
                    subscriptions: action.payload.authObj.authUser.account.subscriptions,
                };

                authenticated = action.payload.authObj.authenticated;

                user = action.payload.authObj.authUser.user;

                saveToken(action.payload.authObj.token, 'auth')

                return {
                    ...state,
                    account,
                    authenticated,
                    nodeEnv,
                    user,
                };

            } else if (authObj.action === 'otpSetup') {
                authObj = action.payload.authObj;
                otpAuthUrl = action.payload.authObj.otpAuthUrl;
                authenticated = authObj.authenticated;

                saveToken(action.payload.authObj.token, 'identity')

                return {
                    ...state,
                    authenticated,
                    nodeEnv,
                    otpAuthUrl,
                };

            } else if (authObj.action === 'otpLogin') {

                saveToken(action.payload.authObj.token, 'identity')
                authenticated = action.payload.authObj.authenticated;

                return {
                    ...state,
                    authenticated,
                    nodeEnv,
                };

            } else {
                console.error('authReducer.unknown authObj.action', authObj.action)
                return {
                    ...state,
                };
            }
        }

        case Actions.AUTH_LOGIN_PASSWORD_FAILURE: {

            return {
                ...state,
                account: {},
                user: {},
                nodeEnv: '',
                authenticated: false,
            };
        }

        // -------------------------------------------------------------------
        // logout
        // -------------------------------------------------------------------
        case Actions.AUTH_LOGOUT_REQUEST: {
            return {
                ...state,
            };
        }

        case Actions.AUTH_LOGOUT_SUCCESS: {
            return {
                ...state,
                account: {},
                user: {},
                nodeEnv: '',
                authenticated: false,
            };
        }

        case Actions.AUTH_LOGOUT_FAILURE: {
            return {
                ...state,
                account: {},
                user: {},
                nodeEnv: '',
                authenticated: false,
            };
        }

        // -------------------------------------------------------------------
        // login.token
        // -------------------------------------------------------------------
        case Actions.AUTH_LOGIN_TOKEN_REQUEST: {
            return {
                ...state,
                account: {},
                user: {},
                nodeEnv: '',
                authenticated: false,
            };
        }

        case Actions.AUTH_LOGIN_TOKEN_SUCCESS: {

            authObj = action.payload.authObj;

            if (authObj.action === 'authenticated') {
                account = authObj.authUser.account;
                user = authObj.authUser.user;
                authenticated = authObj.authenticated;

                saveToken(authObj.token, authObj.tokenType);
            }

            nodeEnv = action.payload.nodeEnv;

            return {
                ...state,
                account,
                authenticated,
                nodeEnv,
                user,
            };

        }

        case Actions.AUTH_LOGIN_TOKEN_FAILURE: {
            removeToken('auth');

            return {
                ...state,
                account: {},
                user: {},
                authenticated: false,
            };
        }

        // -------------------------------------------------------------------
        // otpSetup
        // -------------------------------------------------------------------
        case Actions.AUTH_OTP_SETUP_REQUEST: {
            return {
                ...state,
                serverMessage: '',
                otpAuthUrl: '',
            };
        }

        case Actions.AUTH_OTP_SETUP_SUCCESS: {
            qrcImage = action.payload.authObj.qrcImage;

            saveToken(action.payload.authObj.token, 'identity');

            return {
                ...state,
                qrcImage,
            };

        }

        case Actions.AUTH_OTP_SETUP_FAILURE: {
            return {
                ...state,
                serverMessage: '',
                otpAuthUrl: '',
            };
        }

        // -------------------------------------------------------------------
        // otpSubmit
        // -------------------------------------------------------------------
        case Actions.AUTH_OTP_SUBMIT_REQUEST: {
            return {
                ...state,
                errorExists: false,
                serverMessage: '',
            };
        }

        case Actions.AUTH_OTP_SUBMIT_SUCCESS: {

            if (objectHas(action.payload, 'authObj') &&
                objectExists(action.payload.authObj)) {

                authObj = action.payload.authObj;
                nodeEnv = action.payload.nodeEnv;

                if (authObj.action === 'authenticated') {

                    saveToken(action.payload.authObj.token, 'auth');
                    removeToken('identity');

                    account = {
                        accountName: action.payload.authObj.authUser.account.accountName,
                        subscriptions: action.payload.authObj.authUser.account.subscriptions,
                    };

                    user = action.payload.authObj.authUser.user;

                    authenticated = action.payload.authObj.authenticated;

                    return {
                        ...state,
                        account,
                        authenticated,
                        nodeEnv,
                        user,
                        errorExists: action.payload.errorExists,
                        serverMessage: action.payload.serverMsg,
                    };

                } else {
                    return {
                        ...state,
                    };
                }

            } else {
                return {
                    ...state,
                };
            }

        }

        case Actions.AUTH_OTP_SUBMIT_FAILURE: {
            return {
                ...state,
                serverMessage: '',
            };
        }

        default: {
            return state;
        }
    }
};

export default authReducer;
